import { useRef, useState } from 'react';
import cx from 'classnames';
import { Menu, MenuItem } from '../Menu';
import './SplitButton.css';

const ChevronDown = (
  <svg
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.5'
    aria-hidden='true'
  >
    <polyline points='6 9 12 15 18 9' />
  </svg>
);

/**
 * QHS SplitButton
 * ---------------
 * Primary action with a dropdown of related variants. Used as
 * "+ New ..." on listing pages where one action is the most common
 * next step but the user occasionally wants a sibling action
 * (e.g. New Assessment / New Order / New Resource on the Patient
 * Dashboard). Visual: gradient primary + a 1px white divider
 * between main and toggle.
 *
 *   <SplitButton
 *     label="New Assessment"
 *     startIcon={<PlusIcon />}
 *     onClick={() => openCreateAssessment()}
 *     menuItems={[
 *       { key: 'order', label: 'New Order', onClick: ... },
 *       { key: 'resource', label: 'New Resource', onClick: ... },
 *     ]}
 *   />
 *
 * Disabled state disables both halves; per-item disabled goes on
 * the menu item itself.
 */
const SplitButton = ({
  label,
  startIcon,
  onClick,
  menuItems = [],
  disabled = false,
  className,
  ...rest
}) => {
  const toggleRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleItemClick = (item) => {
    setOpen(false);
    item.onClick?.();
  };

  return (
    <div className={cx('qhs-split-btn', className)} {...rest}>
      <button
        type='button'
        className='qhs-split-btn__main'
        onClick={onClick}
        disabled={disabled}
      >
        {startIcon && <span className='qhs-split-btn__icon'>{startIcon}</span>}
        <span>{label}</span>
      </button>
      <button
        ref={toggleRef}
        type='button'
        aria-haspopup='menu'
        aria-expanded={open}
        className='qhs-split-btn__toggle'
        onClick={() => setOpen((v) => !v)}
        disabled={disabled || menuItems.length === 0}
      >
        <span className={cx('qhs-split-btn__chevron', open && 'qhs-split-btn__chevron--open')}>
          {ChevronDown}
        </span>
      </button>
      {open && (
        <Menu
          anchorEl={toggleRef.current}
          open={open}
          onClose={() => setOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {menuItems.map((item) => (
            <MenuItem
              key={item.key}
              onClick={() => handleItemClick(item)}
              disabled={item.disabled}
            >
              {item.icon && <span className='qhs-split-btn__menu-icon'>{item.icon}</span>}
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </div>
  );
};

export default SplitButton;
